<template>
  <div class="grid">
    <div
      class="col-12 md:col-5"
    >
      <label
        for="site-filter"
        class="block"
      >{{ $t('entity.office.office') }} : </label>
      <Dropdown
        id="office-filter"
        v-model="selectedOffice"
        :options="sortedOffices"
        :option-label="(office) => office.site.name + ' : ' + office.name"
        :show-clear="true"
        class="w-full"
      />
    </div>

    <div
      class="col-12 md:col-5"
    >
      <label
        for="roaming-team-filter"
        class="block"
      >{{ $t('entity.appointment.roamingTeam') }} : </label>
      <Dropdown
        id="roaming-team-filter"
        v-model="selectedRoamingTeam"
        :options="roamingTeams"
        :option-label="(roamingTeam) => roamingTeam.name"
        :show-clear="true"
        class="w-full"
      />
    </div>
  </div>


  <FullCalendar
    ref="calendar"
    :options="calendarOptions"
  >
    <template #eventContent="arg">
      <div
        class="p-0 m-0 pl-1 cursor-pointer min-h-full event-container"
        @click="selectAppointment(arg.event.extendedProps.appointment)"
      >
        <p
          v-if="arg.event.extendedProps.appointment"
          class="p-0 m-0 breakline"
        >
          {{ getCompany(arg.event.extendedProps.appointment) }} -
          {{ arg.event.extendedProps.appointment.request.amountOfEquipment }}
          {{ $t('appointmentPage.equipment.equipments') }}
        </p>
      </div>
    </template>
  </FullCalendar>
</template>

<script>
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import allLocales from "@fullcalendar/core/locales-all";
import Dropdown from "primevue/dropdown";
import FullCalendar from "@fullcalendar/vue3";
import {reactive} from "vue";
import useAppointmentFilter from "@/composables/filters/useAppointmentFilter";
import useAppointmentStatus from "@/composables/useAppointmentStatus";
import useOfficeFilter from "@/composables/filters/useOfficeFilter";
import useRoamingTeamFilter from "@/composables/filters/useRoamingTeamFilter";

export default {
  name: 'AgentCalendar',
  components: { FullCalendar, Dropdown },
  emits: ['selectAppointment'],
  setup(){
    const officeFilter = reactive(useOfficeFilter());
    officeFilter.pageable.size = 1000;
    const roamingTeamFilter = reactive(useRoamingTeamFilter());

    const appointmentFilter = reactive(useAppointmentFilter());
    const appointmentStatus = useAppointmentStatus();
    appointmentFilter.pageable.size = 1000;
    appointmentFilter.filters.validated.value = true;
    appointmentFilter.filters.status.value = [appointmentStatus.NO_SHOW, appointmentStatus.HONORED];
    return {
      officeFilter,
      roamingTeamFilter,
      appointmentFilter
    }
  },
  data() {
    return {
      offices: [],
      roamingTeams: [],
      selectedOffice: null,
      selectedRoamingTeam: null,
      appointments: [],
      calendarOptions: {
        headerToolbar: {
          left: '',
          center: 'title',
          right: 'today prev,next'
        },
        plugins: [timeGridPlugin, interactionPlugin],
        initialView: 'timeGridWeek',
        allDaySlot: false,
        locales: allLocales,
        locale: this.$i18n.locale,
        slotMinTime: '08:00',
        slotMaxTime: '21:00',
        scrollTimeReset: false,
        slotEventOverlap: false,
        slotDuration: '00:30:00',
        selectable: false,
        events: async ( fetchInfo, successCallback ) => {
          this.appointmentFilter.filters.start.value = new Date(fetchInfo.start);
          this.appointmentFilter.filters.end.value = new Date(fetchInfo.end);
          const events = await this.getAppointments(fetchInfo)
          successCallback(events)
        }
      },
    }
  },
  computed: {
    sortedOffices() {
      return this.offices.slice().sort((a, b) =>
        a.site.name.toLowerCase().localeCompare(b.site.name.toLowerCase())
      );
    }
  },
  async created() {
    await this.getOffices();
    await this.getRoamingTeams();
    this.$watch('$i18n.locale', (newVal) => this.calendarOptions.locale = newVal)
    this.$watch('selectedOffice', () => {
      if (this.selectedOffice !== null) this.selectedRoamingTeam = null;
      this.reloadAppointments();
    })
    this.$watch('selectedRoamingTeam', () => {
        if (this.selectedRoamingTeam !== null) this.selectedOffice = null;
      this.reloadAppointments();
    });
    window.addEventListener('resize', this.setCalendarView)
  },
  unmounted() {
    window.removeEventListener('resize', this.setCalendarView)
  },
  methods: {
    reloadAppointments() {
      if (this.$refs.calendar) {
        this.$refs.calendar.getApi().refetchEvents()
      }
    },

    async getAppointments(){
      this.appointmentFilter.filters.officeId.value = this.selectedOffice?.id;
      this.appointmentFilter.filters.roamingTeamId.value = this.selectedRoamingTeam?.id;

      const {content} = await this.$appointmentService.getAll(this.appointmentFilter.getQueryParams());
      this.appointments = content;
      return this.appointments.map(appointment => {
        return {
          id: appointment.id,
          start: new Date(appointment.start),
          end: new Date(appointment.start.split('T')[0] + 'T' + appointment.end),
          appointment: appointment
        }
      })
    },

    //Récupération des bureaux mobiles et leur site associé
    async getOffices(){
      const res = await this.$officeService.getAll(this.officeFilter.getQueryParams());
      this.offices = res.content
    },

    async getRoamingTeams(){
      this.roamingTeams = (await this.$roamingTeamService.fetchRoamingTeamsPagingAndSorting(this.roamingTeamFilter.getQueryParams())).content;
    },

    setCalendarView() {
      // En vue mobile, le calendrier n'affiche qu'une seule journée.
      const mobileBreakpoint = 576;
      this.windowWidth = window.innerWidth;
      let fullCalendarApi = this.$refs.calendar.getApi();
      if (this.windowWidth < mobileBreakpoint) {
        fullCalendarApi.changeView('timeGridDay');
      } else {
        fullCalendarApi.changeView('timeGridWeek');
      }
    },

    selectAppointment(appointment) {
      this.$emit('selectAppointment', appointment)
    },

    getCompany(appointment) {
      return appointment.specialRequestContact ?
        appointment.specialRequestContact.company :
        appointment.request.user.company
    }
  }
}
</script>

<style lang="scss" scoped>
.breakline {
  word-break: break-word;
}
</style>
